import type {
  BarChartProps,
  HistogramProps,
  LineChartProps,
  MetricDatum,
  PieChartProps,
  ScatterPlotProps,
  SummaryCardProps,
} from '@captivateiq/visualizations';

import type {
  BarChartBlock,
  HistogramBlock,
  LineChartBlock,
  PieChartBlock,
  ScatterPlotBlock,
  SummaryCardBlock,
} from '@captivateiq/assistant/types';

type AgentFormat = 'currency' | 'number' | 'percentage' | undefined;

type NumberFormat = { type?: string; currency_code?: string; comma?: boolean };

const DEFAULT_CURRENCY_CODE = 'USD';

const toNumberFormat = (format: AgentFormat, currencyCode?: string): NumberFormat | undefined => {
  if (!format) return undefined;
  if (format === 'currency')
    return { type: 'currency', currency_code: currencyCode ?? DEFAULT_CURRENCY_CODE, comma: true };
  if (format === 'percentage') return { type: 'percentage' };
  return { type: 'number', comma: true };
};

const pivotLabelValueData = (
  data: Array<{ label: string; value: number; series?: string }>,
): { metric: MetricDatum[]; seriesKeys: string[] } => {
  const hasSeries = data.some((d) => Boolean(d.series));
  if (!hasSeries) {
    return {
      metric: data.map((d, index) => ({ index, label: d.label, value: d.value })),
      seriesKeys: ['value'],
    };
  }

  const seriesOrder: string[] = [];
  data.forEach((d) => {
    if (d.series && !seriesOrder.includes(d.series)) seriesOrder.push(d.series);
  });

  const grouped = new Map<string, MetricDatum>();
  data.forEach((d) => {
    const existing = grouped.get(d.label);
    if (existing) {
      if (d.series) (existing as Record<string, unknown>)[d.series] = d.value;
    } else {
      const datum: MetricDatum = { index: grouped.size, label: d.label };
      if (d.series) (datum as Record<string, unknown>)[d.series] = d.value;
      grouped.set(d.label, datum);
    }
  });

  return { metric: Array.from(grouped.values()), seriesKeys: seriesOrder };
};

export const chartBlockToBarChartProps = (block: BarChartBlock): BarChartProps => {
  const { metric, seriesKeys } = pivotLabelValueData(block.data);
  const tickFormat = toNumberFormat(block.yAxisFormat, block.currencyCode) ?? { type: 'number' };
  return {
    data: metric,
    xAxisKeys: ['label'],
    xAxisLabels: block.xAxisLabel ? [block.xAxisLabel] : undefined,
    yAxisKeys: seriesKeys,
    yAxisLabels: seriesKeys.map((key) => (key === 'value' ? block.yAxisLabel ?? '' : key)),
    orientation: block.orientation ?? 'vertical',
    targetLine: block.targetLine ?? null,
    scales: { y: { tickFormat } },
  };
};

export const chartBlockToLineChartProps = (block: LineChartBlock): LineChartProps => {
  const { metric, seriesKeys } = pivotLabelValueData(block.data);
  const tickFormat = toNumberFormat(block.yAxisFormat, block.currencyCode) ?? { type: 'number' };
  return {
    data: metric,
    xAxisKeys: ['label'],
    xAxisLabels: block.xAxisLabel ? [block.xAxisLabel] : undefined,
    yAxisKeys: seriesKeys,
    yAxisLabels: seriesKeys.map((key) => (key === 'value' ? block.yAxisLabel ?? '' : key)),
    targetLine: block.targetLine ?? null,
    scales: { y: { tickFormat } },
  };
};

export const chartBlockToPieChartProps = (block: PieChartBlock): PieChartProps => ({
  data: block.data.map((d, index) => ({ index, label: d.label, value: d.value })),
  dataKeys: ['value'],
  nameKey: 'label',
  format: toNumberFormat(block.valueFormat, block.currencyCode),
});

export const chartBlockToScatterPlotProps = (block: ScatterPlotBlock): ScatterPlotProps => {
  const tickFormat = toNumberFormat(block.yAxisFormat, block.currencyCode) ?? { type: 'number' };
  return {
    data: block.data.map((d, index) => ({
      index,
      x: d.x,
      y: d.y,
      label: d.label ?? `${d.x},${d.y}`,
      ...(d.series ? { series: d.series } : {}),
    })),
    xAxisKeys: ['x'],
    xAxisLabels: block.xAxisLabel ? [block.xAxisLabel] : undefined,
    yAxisKeys: ['y'],
    yAxisLabels: [block.yAxisLabel ?? ''],
    scales: { y: { tickFormat } },
  };
};

export const chartBlockToHistogramProps = (block: HistogramBlock): HistogramProps => {
  const tickFormat = toNumberFormat(block.xAxisFormat, block.currencyCode) ?? { type: 'number' };
  return {
    data: block.data.map((d, index) => ({ index, value: d.value })),
    xAxisKeys: ['value'],
    yAxisKeys: ['value'],
    yAxisLabels: [block.yAxisLabel ?? 'Count'],
    scales: { y: { tickFormat } },
  };
};

export const chartBlockToSummaryCardProps = (block: SummaryCardBlock): SummaryCardProps => ({
  data: [{ index: 0, _summary_field: 'value', _summary_value: block.value, label: block.title }],
  title: block.title,
  showTitle: true,
  format: toNumberFormat(block.format, block.currencyCode),
  supportingNumbers: block.comparison
    ? [
        {
          compareAs: block.comparison.compareAs,
          label: block.comparison.label,
          value: String(block.comparison.value),
        },
      ]
    : undefined,
  sparkline: block.sparkline ? { enabled: true, data: block.sparkline, type: 'line' } : undefined,
});
